import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  CompanyReadModel,
  useGetBuildingsByIdInvestorQuery,
  useGetBuildingsByIdManagerQuery,
  useGetBuildingsByIdOwnerQuery,
  usePutSpacesByIdInvestorMutation,
  usePutSpacesByIdManagerMutation,
  usePutSpacesByIdOwnerMutation,
} from '@client/api';
import { PageDetailContent } from '@client/toolkit';

import { PageContentAnimation } from '../../../../../components';
import { CompaniesCard } from '../../../components/companies-card/companies-card.component';

export function BuildingCompaniesRolesRoute() {
  const { buildingId } = useParams<{ buildingId: string }>();
  const { t: tType } = useTranslation('type');
  const { t: tNotification } = useTranslation('notification');

  const {
    data: buildingOwners,
    isFetching: isFetchingOwners,
    refetch: refetchOwners,
  } = useGetBuildingsByIdOwnerQuery({ id: buildingId ?? '' }, { skip: !buildingId });

  const {
    data: buildingInvestors,
    isFetching: isFetchingInvestors,
    refetch: refetchInvestors,
  } = useGetBuildingsByIdInvestorQuery({ id: buildingId ?? '' }, { skip: !buildingId });

  const {
    data: buildingManager,
    isFetching: isFetchingManager,
    refetch: refetchManager,
  } = useGetBuildingsByIdManagerQuery({ id: buildingId ?? '' }, { skip: !buildingId });

  const [putOwners] = usePutSpacesByIdOwnerMutation();
  const [putInvestors] = usePutSpacesByIdInvestorMutation();
  const [putManagers] = usePutSpacesByIdManagerMutation();

  const handleEditOwners = (data: CompanyReadModel[]) => {
    putOwners({ id: buildingId ?? '', body: { owners: data.map((c) => c.id) } })
      .unwrap()
      .then(() => {
        refetchOwners();
        toast.success(tNotification('editing.fulfilled'));
      });
  };

  const handleEditInvestors = (data: CompanyReadModel[]) => {
    putInvestors({ id: buildingId ?? '', body: { investors: data.map((c) => c.id) } })
      .unwrap()
      .then(() => {
        refetchInvestors();
        toast.success(tNotification('editing.fulfilled'));
      });
  };

  const handleEditManager = (data: CompanyReadModel[]) => {
    putManagers({ id: buildingId ?? '', body: { tenantId: data[0]?.id ?? '' } })
      .unwrap()
      .then(() => {
        refetchManager();
        toast.success(tNotification('editing.fulfilled'));
      });
  };

  return (
    <PageDetailContent align="left">
      <PageContentAnimation className="flex flex-col gap-6">
        <CompaniesCard
          role="Owner"
          isLoading={isFetchingOwners}
          companies={buildingOwners ?? []}
          buildingId={buildingId}
          title={tType('RelationshipType.Owner', { context: 'plural' })}
          onEdit={handleEditOwners}
        />
        <CompaniesCard
          role="Investor"
          isLoading={isFetchingInvestors}
          companies={buildingInvestors ?? []}
          buildingId={buildingId}
          title={tType('RelationshipType.Investor', { context: 'plural' })}
          onEdit={handleEditInvestors}
        />
        <CompaniesCard
          role="Manager"
          isLoading={isFetchingManager}
          companies={buildingManager ? [buildingManager] : []}
          buildingId={buildingId}
          title={tType('RelationshipType.Manager', { context: 'plural' })}
          onEdit={handleEditManager}
        />
      </PageContentAnimation>
    </PageDetailContent>
  );
}
